import type {
  AwardFilterOption,
  AwardFilterOptionsResponse,
  AwardShortlistTag,
  AwardState,
  AwardStatus,
} from "../../api/awards.types.js";
import type { FilterConfig } from "../../components/DataFilters/DataFilters.js";
import type { CountryLabelResolver } from "./useCountryNames.js";

/**
 * "No filter" is a *value*, not the absence of one. Three separate pieces of
 * machinery already agree on this exact spelling, which is why every filter here
 * is a plain string rather than, say, a boolean:
 *
 *   - `useUrlParams.updateUrl` deletes a param whose value is `"all"`, so the
 *     unfiltered view has one URL rather than `?status=all`;
 *   - `useFilters` drops `"all"` from the active-filter chips;
 *   - `awards.service.ts` refuses to put it on the wire.
 *
 * A boolean filter would satisfy `useFilters` (`Record<string, string | boolean>`)
 * but not `DataFilters.values` (`Record<string, string | number>`).
 */
export const ALL_FILTER_VALUE = "all";

/**
 * Splits a multi-select filter's URL value. `"all"`, an empty string and blank
 * entries all mean "no values" — the URL is typed by people as well as written
 * by the dropdown, so `?status=winner,,shortlisted,` has to read cleanly.
 */
export function readCsvFilter(value: string): string[] {
  if (value === ALL_FILTER_VALUE) return [];
  return value
    .split(",")
    .map(entry => entry.trim())
    .filter(entry => entry.length > 0);
}

/**
 * The two closed enums the UI owns outright. They ship with the page instead of
 * with `awards/filter-options` (Product / Award Series / Type of Award / Year),
 * because nothing has to be fetched to know them — they are the `status` and
 * `state` columns of `research.awards`.
 *
 * One list per enum, holding the wire value *and* its label: the dropdown, the
 * chip label and the "is this a value we recognise" check below all read from
 * it, so they cannot drift apart.
 */
const STATUS_OPTIONS: readonly { value: AwardStatus; label: string }[] = [
  { value: "winner", label: "Winner" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "highly_commended", label: "Highly Commended" },
];

const PUBLISH_STATUS_OPTIONS: readonly { value: AwardState; label: string }[] = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

/** The third closed enum, on the same terms: a shortlisted row's internal mark.
 * A row without one never matches this filter, the same way an unmatched row
 * never matches the Firm filter. */
const SHORTLIST_TAG_OPTIONS: readonly { value: AwardShortlistTag; label: string }[] = [
  { value: "predicted_winner", label: "Predicted winner" },
  { value: "runner_up", label: "Runner-up" },
];

/** The one recognised value for the Linking filter — not a closed enum from the
 * database like the two above, just the single wire value `toIncompleteFilter`
 * matches against. */
const LINKING_OPTIONS: readonly { value: "incomplete"; label: string }[] = [
  { value: "incomplete", label: "Only incomplete" },
];

/**
 * The filter state the page holds, keyed by the URL param each filter is backed
 * by. `publishStatus` is deliberately *not* called `state`: `state` is the API's
 * name for it, but as a URL key it reads like router state, and the page has to
 * name the filter something a person can recognise in an address bar. `type` is
 * the same kind of translation: a person reads `?type=rising-star` in the address
 * bar, while `awardType` is the API's name for the same filter. `linking` is
 * another one again: it is the UI's word for the thing the rows already mark as
 * "needs linking", while `incomplete` is the API's word for it. `jurisdiction` is
 * the same kind of translation again: it is the person's word for the API's
 * `countryId`, in the same family as `type`/`awardType`. `firm` carries an
 * Address Book company uuid and is the person's word for the API's `companyId`;
 * `lawyer` carries an Address Book contact uuid and is the person's word for the
 * API's `contactId`.
 *
 * No key may be called `edition` — `DataFilters` hard-codes a narrow width for
 * that one key (`filterDropdownSmall`).
 */
export type AwardFilterValues = {
  product: string;
  type: string;
  series: string;
  firm: string;
  lawyer: string;
  status: string;
  shortlistTag: string;
  publishStatus: string;
  year: string;
  jurisdiction: string;
  linking: string;
};

/**
 * Chip labels, one per filter key. `useFilters` falls back to
 * `key.charAt(0).toUpperCase() + key.slice(1)` when a key is missing here, which
 * would put "PublishStatus" on the chip — so every key needs an explicit entry.
 *
 * Hand-written and typed `Record<keyof AwardFilterValues, string>`, rather than
 * derived from the dropdown configs the way it used to be: `buildAwardFilters`
 * below runs per render — its output depends on `awards/filter-options`, which
 * arrives after mount — so there is no longer one fixed config list to derive
 * from at module load. `useFilters` lists this object in a `useMemo` dependency
 * array, so it stays a stable, module-level identity; the `Record` type is what
 * makes the compiler catch a filter added without a label, the guarantee the old
 * derivation used to give for free.
 */
export const AWARD_FILTER_LABELS: Record<keyof AwardFilterValues, string> = {
  product: "Product",
  type: "Type of Award",
  series: "Award Series",
  firm: "Firm",
  lawyer: "Associated Lawyer",
  status: "Status",
  shortlistTag: "Shortlist tag",
  publishStatus: "Publish Status",
  year: "Year",
  jurisdiction: "Jurisdiction(s)",
  linking: "Linking",
};

/**
 * Everything the dropdown row is built from: the `awards/filter-options`
 * response — `null` while loading and after a failure, since
 * `useAwardFilterOptions` never yields a partial object — and the country-name
 * resolver the page already holds for the table's Jurisdictions chips, reused
 * here for the Jurisdiction(s) dropdown's option labels.
 */
export type AwardFilterSources = {
  options: AwardFilterOptionsResponse | null;
  loading: boolean;
  getCountryLabel: CountryLabelResolver;
  /** The firm the URL currently names, once its Address Book name is known;
   * `null` when no firm is selected or the name has not resolved (yet). */
  selectedFirm: { id: string; name: string } | null;
  /** The lawyer the URL currently names, once their Address Book name is known;
   * `null` when no lawyer is selected or the name has not resolved (yet). */
  selectedLawyer: { id: string; name: string } | null;
};

/** Hint shown under the five dictionary dropdowns while they cannot be used. */
export const FILTER_OPTIONS_LOADING_HINT = "Loading filter options…";
export const FILTER_OPTIONS_FAILED_HINT = "Couldn't load filter options";

/**
 * The dropdown row, in the mock's order. Product / Type of Award / Award Series /
 * Year / Jurisdiction(s) take their options from `awards/filter-options` — only
 * values at least one award uses, so no option ever returns an empty list — and
 * are disabled with a hint until that response is in; if it never comes, they
 * stay disabled and the rest of the page, the list above all, carries on. Firm
 * sits right after Award Series and Associated Lawyer right after Firm: Address
 * Book typeaheads rather than dropdowns built from a response, so neither ever
 * depends on `filter-options` at all. Status, Shortlist tag and Publish Status
 * are the closed enums the UI owns, the tag right after the status it qualifies;
 * Linking is a single select appended last — the mock has no such filter.
 */
export function buildAwardFilters({
  options,
  loading,
  getCountryLabel,
  selectedFirm,
  selectedLawyer,
}: AwardFilterSources): FilterConfig[] {
  const unavailable: Pick<FilterConfig, "disabled" | "title"> =
    options === null
      ? {
          disabled: true,
          title: loading ? FILTER_OPTIONS_LOADING_HINT : FILTER_OPTIONS_FAILED_HINT,
        }
      : {};

  const dictionary = (
    key: keyof AwardFilterValues,
    allLabel: string,
    entries: { value: string; label: string }[],
  ): FilterConfig => ({
    key,
    label: AWARD_FILTER_LABELS[key],
    multiple: true,
    options: [{ value: ALL_FILTER_VALUE, label: allLabel }, ...entries],
    ...unavailable,
  });

  const slugOptions = (entries: AwardFilterOption[] | undefined) =>
    (entries ?? []).map(entry => ({ value: entry.slug, label: entry.name }));

  return [
    dictionary("product", "All products", slugOptions(options?.products)),
    dictionary("type", "All types", slugOptions(options?.awardTypes)),
    dictionary("series", "All series", slugOptions(options?.series)),
    // Firm and Associated Lawyer are typeaheads, not dropdowns: the page
    // attaches the `CompanyPicker` and the `AddressBookContactPicker` as these
    // two entries' `control`, and they are not built here because they are JSX
    // bound to the render's own handlers. Each options list below exists for
    // the chip alone — `getAwardFilterOptionLabel` reads the selected firm's or
    // lawyer's name from it, so the chip says "Firm: Bannerman Quinn" rather
    // than the uuid — and until the name resolves the list holds only the
    // sentinel, which leaves the chip on the raw uuid, the same fallback the
    // dictionary chips use for a raw slug. Neither is ever disabled: both
    // depend on the Address Book, not on `filter-options`.
    {
      key: "firm",
      label: AWARD_FILTER_LABELS.firm,
      options: [
        { value: ALL_FILTER_VALUE, label: "All firms" },
        ...(selectedFirm ? [{ value: selectedFirm.id, label: selectedFirm.name }] : []),
      ],
    },
    {
      key: "lawyer",
      label: AWARD_FILTER_LABELS.lawyer,
      options: [
        { value: ALL_FILTER_VALUE, label: "All lawyers" },
        ...(selectedLawyer ? [{ value: selectedLawyer.id, label: selectedLawyer.name }] : []),
      ],
    },
    {
      key: "status",
      label: AWARD_FILTER_LABELS.status,
      multiple: true,
      options: [{ value: ALL_FILTER_VALUE, label: "All statuses" }, ...STATUS_OPTIONS],
    },
    {
      key: "shortlistTag",
      label: AWARD_FILTER_LABELS.shortlistTag,
      multiple: true,
      options: [{ value: ALL_FILTER_VALUE, label: "All tags" }, ...SHORTLIST_TAG_OPTIONS],
    },
    {
      key: "publishStatus",
      label: AWARD_FILTER_LABELS.publishStatus,
      multiple: true,
      options: [
        { value: ALL_FILTER_VALUE, label: "All publish statuses" },
        ...PUBLISH_STATUS_OPTIONS,
      ],
    },
    dictionary(
      "year",
      "All years",
      (options?.years ?? []).map(year => ({ value: String(year), label: String(year) })),
    ),
    // `countryIds` arrives ascending by id, which means nothing to a person, so
    // this one dictionary sorts by label instead of keeping response order the
    // way the others do. The labels come from the region-countries tree the page
    // already fetches for the table's Jurisdictions chips (`useCountryNames`), so
    // while that tree is in flight or down every option reads `Country ${id}` and
    // the list corrects itself the moment `getCountryLabel`'s identity changes —
    // the tree never disables this dropdown, because a fallback label is still a
    // usable option. Only `filter-options` (via `unavailable` above) does that.
    dictionary(
      "jurisdiction",
      "All jurisdictions",
      (options?.countryIds ?? [])
        .map(countryId => ({ value: String(countryId), label: getCountryLabel(countryId) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ),
    {
      key: "linking",
      label: AWARD_FILTER_LABELS.linking,
      options: [{ value: ALL_FILTER_VALUE, label: "All rows" }, ...LINKING_OPTIONS],
    },
  ];
}

/**
 * Reads the filter state out of the URL, which is where the page keeps it: the
 * dropdowns, the chips and the request all render from one call to this per
 * navigation, so a pasted `/awards?status=winner` opens filtered and a Back
 * button moves all three at once. Keeping it here keeps the URL param names next
 * to the filter configs they belong to.
 */
export function readAwardFilters(searchParams: URLSearchParams): AwardFilterValues {
  return {
    product: searchParams.get("product") || ALL_FILTER_VALUE,
    type: searchParams.get("type") || ALL_FILTER_VALUE,
    series: searchParams.get("series") || ALL_FILTER_VALUE,
    firm: searchParams.get("firm") || ALL_FILTER_VALUE,
    lawyer: searchParams.get("lawyer") || ALL_FILTER_VALUE,
    status: searchParams.get("status") || ALL_FILTER_VALUE,
    shortlistTag: searchParams.get("shortlistTag") || ALL_FILTER_VALUE,
    publishStatus: searchParams.get("publishStatus") || ALL_FILTER_VALUE,
    year: searchParams.get("year") || ALL_FILTER_VALUE,
    jurisdiction: searchParams.get("jurisdiction") || ALL_FILTER_VALUE,
    linking: searchParams.get("linking") || ALL_FILTER_VALUE,
  };
}

/**
 * Every filter key at its sentinel, plus `page` emptied: the one `updateUrl`
 * call "Clear all" makes. One call rather than one per key because each
 * `updateUrl` is a navigation that refetches the list. Typed as the `updateUrl`
 * argument it is, and built from the keys of `AWARD_FILTER_LABELS`, so a filter
 * added there is cleared here without a second list to maintain.
 */
export function clearedAwardFilters(): Record<string, string> {
  const cleared: Record<string, string> = { page: "" };
  for (const key of Object.keys(AWARD_FILTER_LABELS)) {
    cleared[key] = ALL_FILTER_VALUE;
  }
  return cleared;
}

/**
 * The dropdown label for a filter's CSV, for the chip: "Status: Winner,
 * Shortlisted" rather than "Status: winner,shortlisted". Splits the value with
 * `readCsvFilter` and keeps the option order of the filter's own config — the
 * same reason `toAwardStatuses`/`toAwardStates` filter the OPTIONS list rather
 * than the URL list — so `?status=highly_commended,winner` reads the same chip
 * as `?status=winner,highly_commended`. A value the config does not know is
 * dropped from the chip, same as the dropdown drops it from the selection; if
 * nothing recognisable survives (a chip for `?status=nonsense`) this falls back
 * to the raw value string, because a chip showing a raw value is better than a
 * chip showing nothing. For the five dictionary filters that same raw fallback is
 * also what a chip shows while `filter-options` has not loaded yet — a slug such
 * as "rising-star" beats showing nothing, and it corrects itself into "Rising
 * Star" the moment the real names land.
 *
 * Takes the built configs as a parameter rather than reading a module constant:
 * the dictionary filters' options depend on `awards/filter-options`, so there is
 * no longer one fixed list to search at module scope.
 */
export function getAwardFilterOptionLabel(
  key: string,
  value: string | number,
  filters: FilterConfig[],
): string {
  const config = filters.find(entry => entry.key === key);
  const wanted = new Set(readCsvFilter(String(value)));
  const labels =
    config?.options
      .filter(option => wanted.has(String(option.value)))
      .map(option => option.label) ?? [];

  return labels.length > 0 ? labels.join(", ") : String(value);
}

/**
 * Narrows a multi-select filter's CSV to the API vocabulary. The URL can carry
 * anything at all — unknown values and the `"all"` sentinel (via `readCsvFilter`)
 * are dropped — and when nothing recognisable is left the result is `undefined`,
 * so `awards.service.ts` sends no param at all rather than an empty one.
 *
 * Filters the OPTIONS list rather than the URL list on purpose: the result comes
 * out in option order and de-duplicated, so `?status=shortlisted,winner,winner`
 * and the dropdown's own `winner,shortlisted` produce the same request.
 */
export function toAwardStatuses(value: string): AwardStatus[] | undefined {
  const wanted = new Set(readCsvFilter(value));
  const statuses = STATUS_OPTIONS.filter(option => wanted.has(option.value)).map(
    option => option.value,
  );
  return statuses.length > 0 ? statuses : undefined;
}

export function toAwardStates(value: string): AwardState[] | undefined {
  const wanted = new Set(readCsvFilter(value));
  const states = PUBLISH_STATUS_OPTIONS.filter(option => wanted.has(option.value)).map(
    option => option.value,
  );
  return states.length > 0 ? states : undefined;
}

export function toAwardShortlistTags(value: string): AwardShortlistTag[] | undefined {
  const wanted = new Set(readCsvFilter(value));
  const tags = SHORTLIST_TAG_OPTIONS.filter(option => wanted.has(option.value)).map(
    option => option.value,
  );
  return tags.length > 0 ? tags : undefined;
}

/**
 * Slug filters go to the API verbatim: awards-api answers an unknown slug with
 * zero rows rather than a 400, and a slug that is valid but currently unused is
 * exactly the same case, so there is nothing for the UI to validate against —
 * `filter-options` only lists the values in use. Trimmed, de-duplicated, in URL
 * order; `undefined` when nothing is left so the service sends no param.
 */
export function toSlugFilter(value: string): string[] | undefined {
  const slugs = [...new Set(readCsvFilter(value))];
  return slugs.length > 0 ? slugs : undefined;
}

/**
 * Shared by `toYears` and `toCountryIds`: both filters must be whole numbers —
 * awards-api answers a non-integer `year` or `countryId` with a 400 — so
 * anything that is not an integer is dropped here, and nothing left means no
 * param. De-duplicated, in URL order.
 */
function toIntegers(value: string): number[] | undefined {
  const integers = [
    ...new Set(
      readCsvFilter(value)
        .map(entry => Number(entry))
        .filter(entry => Number.isInteger(entry)),
    ),
  ];
  return integers.length > 0 ? integers : undefined;
}

/** Years must be whole numbers — awards-api validates `year` and answers
 * `?year=abc` with a 400. */
export function toYears(value: string): number[] | undefined {
  return toIntegers(value);
}

/** Jurisdictions are taxonomy country ids, whole numbers for the same reason
 * years are — awards-api answers `?countryId=abc` with a 400. */
export function toCountryIds(value: string): number[] | undefined {
  return toIntegers(value);
}

/**
 * `true` only for the one value that means "narrow to rows still missing a side";
 * the `"all"` sentinel and any stray URL value mean no filter, which the service
 * expresses as no parameter at all. There is deliberately no `false` on the wire:
 * awards-api treats `incomplete=false` exactly like an absent parameter.
 */
export function toIncompleteFilter(value: string): true | undefined {
  return LINKING_OPTIONS.some(option => option.value === value) ? true : undefined;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Serves `firm` and `lawyer` alike. awards-api validates `companyId` and
 * `contactId` with `z.uuid()` and answers anything else with a 400, so a value
 * that is not one uuid is dropped here and the service sends no param at all.
 * One value, not a CSV — the API accepts a CSV, but this round's pickers each
 * select a single firm or lawyer, so `?firm=a,b` and `?lawyer=a,b` are garbage
 * too; the `"all"` sentinel fails the pattern on its own, same as every other
 * filter's sentinel. The value is passed through unchanged (no lower-casing), so
 * the URL and the wire carry the same string.
 */
export function toUuid(value: string): string | undefined {
  return UUID_PATTERN.test(value) ? value : undefined;
}
